import { ReactElement, useCallback, useEffect, useState } from 'react';

import { ChakraBox, ChakraGridItem, ChakraSimpleGrid } from '@equip.health/ui';
import { useHistory } from 'react-router-dom';

import { ApiCommand } from '~/lib/Api';
import { Listeners } from '~/lib/constants/global.constants';
import { PatientTabs } from '~/lib/constants/patients.constants';
import { PatientTabsCollection } from '~/lib/hooks/usePatientTabsCollection';
import { OrganizeBy } from '~/lib/constants/task.constants';
import urlConstants from '~/lib/constants/url.constants';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { useTaskContext } from '~/lib/contexts/TaskContext';
import { useApi } from '~/lib/hooks';
import {
  CompServTask,
  FetchTasksResponse,
  getEmptyTaskContent,
  mapCompServTaskToTask,
} from '~/lib/util/task.util';
import Loading from '~/pages/loading';
import { Path } from '~/routes/constants';

import EmptyTaskList from '../../../tasks/EmptyTaskList';
import TaskList from '../../../tasks/TaskList/TaskList';
import TaskListItem from '../../../tasks/TaskList/TaskListItem';

const TASKS_CONTAINER_HEIGHT = 'calc(100vh - 310px)';
const ALL_TASKS_PAGE_SIZE = 50;

const { listTasks: listTasksUrl } = urlConstants.tasks;

const mapTasksToGridItems = (
  data: CompServTask[],
  categories: Category[],
  onTaskSelect: (taskId: string) => void,
): ReactElement[] => {
  return (data ?? [])
    .map((task) => mapCompServTaskToTask(task, categories))
    .map((task: Task) => (
      <ChakraGridItem
        key={`patient-tasks-${task.taskExternalId}`}
        marginTop="3px"
      >
        <TaskListItem
          isFamilyTask={task.isFamilyTask}
          isPatientProfile
          onTaskSelect={() => onTaskSelect(task.taskExternalId)}
          task={task}
        />
      </ChakraGridItem>
    ));
};

interface PatientTaskListProps {
  isFamilyTask: boolean;
  patientId: string;
  subTabPath: string;
}

const PatientTaskList = ({
  isFamilyTask,
  patientId,
  subTabPath,
}: PatientTaskListProps) => {
  const [tasks, setTasks] = useState<ReactElement[]>([]);
  const [tasksCount, setTasksCount] = useState<number>(null);
  const [tasksPage, setTasksPage] = useState<number>(0);

  const history = useHistory();
  const { categories } = useTaskContext();
  const { addListener, removeListener } = useGlobalContext();

  const onTaskSelect = useCallback(
    (taskId: string) => {
      const tasksPath = `${Path.MAUD_PATIENTS}/${patientId}/${PatientTabsCollection[PatientTabs.TASKS].subPath}`;
      history.push({
        pathname: `${tasksPath}/${subTabPath}/${taskId}`,
        state: { fromPatientRouteTab: true, fromTaskRoute: false },
      });
    },
    [patientId, subTabPath],
  );

  const { sendRequest: getPatientTasksData } = useApi<FetchTasksResponse>();

  const handleFetchResponse = useCallback(
    ({ data, pageNumber, totalCount }: FetchTasksResponse): void => {
      const newItems = mapTasksToGridItems(data, categories, onTaskSelect);
      if (pageNumber === 1) {
        setTasks(newItems);
      } else {
        setTasks((prev) => prev.concat(newItems));
      }
      setTasksPage((prev) => pageNumber ?? prev);
      setTasksCount((prev) => totalCount ?? prev);
    },
    [categories, onTaskSelect],
  );

  const fetchTasks = useCallback(
    (pageNumber?: number): void => {
      if (
        pageNumber === 1 ||
        tasksCount === null ||
        tasks.length < tasksCount
      ) {
        getPatientTasksData({
          callback: handleFetchResponse,
          command: ApiCommand.GET,
          options: {
            'filter-by': {
              'is-family-task': isFamilyTask,
              'patient-external-id': patientId,
            },
            'order-by': isFamilyTask
              ? OrganizeBy.CREATED_DATE
              : OrganizeBy.DUE_DATE,
            'page-number': pageNumber ?? tasksPage + 1,
            'page-size': ALL_TASKS_PAGE_SIZE,
          },
          url: listTasksUrl,
        });
      }
    },
    [
      getPatientTasksData,
      handleFetchResponse,
      isFamilyTask,
      patientId,
      tasks?.length,
      tasksCount,
      tasksPage,
    ],
  );

  useEffect(() => {
    setTasks([]);
    setTasksCount(null);
    setTasksPage(0);
    fetchTasks(1);
  }, [isFamilyTask]);

  useEffect(() => {
    addListener(Listeners.PATIENTTASKS_TASKDETAILS_CREATE, () => fetchTasks(1));
    addListener(Listeners.PATIENTTASKS_TASKDETAILS_UPDATE, () => fetchTasks(1));
    return () => {
      removeListener(Listeners.PATIENTTASKS_TASKDETAILS_CREATE);
      removeListener(Listeners.PATIENTTASKS_TASKDETAILS_UPDATE);
    };
  }, []);

  if (tasksCount === null) {
    return <Loading />;
  }

  if (!tasks?.length) {
    return (
      <ChakraBox paddingTop="36px">
        <EmptyTaskList
          description={getEmptyTaskContent('description', false)}
          primaryText={getEmptyTaskContent('primaryText', false)}
          secondaryText={getEmptyTaskContent('secondaryText', false)}
        />
      </ChakraBox>
    );
  }

  return (
    <ChakraSimpleGrid columns={1} spacing="8px">
      <TaskList
        data={[
          {
            count: tasksCount,
            items: tasks,
            key: patientId,
            title: '',
          },
        ]}
        fetchNextBatch={fetchTasks}
        height={TASKS_CONTAINER_HEIGHT}
        hideSectionHeaders
        totalTasksCount={tasksCount}
      />
    </ChakraSimpleGrid>
  );
};

export default PatientTaskList;
